import { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Save, Edit2, Loader2 } from 'lucide-react';
import { ic, formatDate } from './helpers';

// ─────────────────────────────────────────
// SECTION
// Collapsible card with icon + title + optional action button (e.g. "Edit", "Add").
// ─────────────────────────────────────────
export function Section({ title, icon: Icon, defaultOpen = true, children, action }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Icon size={16} className="text-blue-600" />
          </div>
          <span className="font-semibold text-slate-800 text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>
      {open && <div className="px-6 pb-5 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// ─────────────────────────────────────────
// FIELD
// Renders either a label + value (read mode) or label + input/select (edit mode).
// ─────────────────────────────────────────
export function Field({ label, value, editing, name, onChange, type = 'text', options, isDate }) {
  const displayValue = isDate ? formatDate(value) : value;
  if (editing) {
    if (options) return (
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
        <select name={name} value={value || ''} onChange={onChange} className={ic}>
          <option value="">—</option>
          {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      </div>
    );
    return (
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
        <input type={type} name={name} value={value || ''} onChange={onChange} className={ic} />
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs font-medium text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-800">{displayValue || <span className="text-slate-400 font-normal">—</span>}</p>
    </div>
  );
}

// ─────────────────────────────────────────
// TOGGLE
// On/off switch in edit mode, "Yes"/"No" text in read mode.
// ─────────────────────────────────────────
export function Toggle({ label, checked, onChange, editing }) {
  if (!editing) return (
    <div>
      <p className="text-xs font-medium text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-800">{checked ? 'Yes' : 'No'}</p>
    </div>
  );
  return (
    <label className="flex items-center justify-between py-1 cursor-pointer">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-200'}`}
      >
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </div>
    </label>
  );
}

// ─────────────────────────────────────────
// CBx (small checkbox)
// Compact checkbox used in the Vehicles section for discounts/anti-theft.
// ─────────────────────────────────────────
export function CBx({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
          checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'
        }`}
      >
        {checked && <Check size={9} strokeWidth={3} className="text-white" />}
      </div>
      <span className="text-xs text-slate-600">{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────
// EDIT ACTIONS / EDIT BUTTON
// Used by every editable section to show Save+Cancel (when editing)
// or Edit (when not). Saving spinner via the `saving` prop.
// ─────────────────────────────────────────
export function EditActions({ onSave, onCancel, saving }) {
  return (
    <div className="flex gap-2">
      <button
        onClick={onSave}
        className="flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
      >
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-1 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-100 transition"
      >
        Cancel
      </button>
    </div>
  );
}

export function EditButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-3 py-1 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-100 transition"
    >
      <Edit2 size={11} /> Edit
    </button>
  );
}